import {formatBusinessTimestamp} from 'src/api/business-time'

import {
    archiveBillableLineItem,
    createBillableLineItem,
    createTimeEntry as createJobTimeEntry,
    deleteTimeEntry as deleteJobTimeEntry,
    getJob as getOperationalJob,
    jobScheduleInputToPatch,
    listJobs as listOperationalJobs,
    listJobTimeEntries,
    patchJob,
    updateBillableLineItem,
    updateJobAssetAssignments,
    updateJobAssetRequirements,
    updateJobLocations,
    updateJobWorkerAssignments,
    updateJobWorkerRequirements,
    updateTimeEntry as updateJobTimeEntry,
} from './api'
import {
    mapFinancialToBillable,
    mapLegacyAssignmentsToOperational,
    mapLegacyRequirementsToOperational,
    mapLegacyStatusFilters,
    mapOperationalJobDetail,
    mapOperationalJobListItem,
} from './job-api-adapter'
import {toApiDateString} from './job-schedule-format'
import type {ReconcileLocationInput} from './location-input'
import type {TimeEntryInput, WorkerAssignmentTimeEntries} from './schemas'
import type {
    AssignmentInput,
    FinancialLineItemInput,
    JobDetail,
    JobListFilters,
    JobListItem,
    JobPatchInput,
    JobScheduleInput,
    RequirementInput,
} from './types'

export type JobDataLayer = {
    listJobs: (filters?: JobListFilters) => Promise<JobListItem[]>
    getJob: (id: string) => Promise<JobDetail>
    patchJob: (id: string, values: JobPatchInput) => Promise<JobDetail>
    saveSchedule: (id: string, values: JobScheduleInput) => Promise<JobDetail>
    saveLocations: (id: string, locations: ReconcileLocationInput[]) => Promise<JobDetail>
    saveRequirements: (id: string, requirements: RequirementInput[]) => Promise<JobDetail>
    saveAssignments: (id: string, assignments: AssignmentInput[]) => Promise<JobDetail>
    approveJob: (id: string) => Promise<JobDetail>
    archiveJob: (id: string) => Promise<JobDetail>
    cancelJob: (id: string, reason: string) => Promise<JobDetail>
    reopenJob: (id: string, approvedToStart: boolean) => Promise<JobDetail>
    createFinancialLineItem: (jobId: string, input: FinancialLineItemInput) => Promise<JobDetail>
    updateFinancialLineItem: (
        jobId: string,
        lineItemId: string,
        input: FinancialLineItemInput,
    ) => Promise<JobDetail>
    archiveFinancialLineItem: (jobId: string, lineItemId: string) => Promise<JobDetail>
    listTimeEntries: (jobId: string) => Promise<WorkerAssignmentTimeEntries[]>
    createTimeEntry: (
        jobId: string,
        assignmentId: string,
        input: TimeEntryInput,
    ) => Promise<JobDetail>
    updateTimeEntry: (jobId: string, entryId: string, input: TimeEntryInput) => Promise<JobDetail>
    deleteTimeEntry: (jobId: string, entryId: string) => Promise<JobDetail>
}

export async function resolveDefaultWorkerTypeId(): Promise<string | null> {
    const jobs = await listOperationalJobs()
    for (const job of jobs) {
        const detail = await getOperationalJob(job.id)
        const workerRequirement = detail.workerRequirements[0]
        if (workerRequirement) {
            return workerRequirement.workerTypeId
        }
    }
    return null
}

function createServerJobDataLayer(): JobDataLayer {
    return {
        async listJobs(filters = {}) {
            const operationalFilters = {
                name: filters.name,
                customerId: filters.customerId,
                startDateFrom: filters.startDateFrom,
                startDateTo: filters.startDateTo,
                sortBy: filters.sortBy === 'start_date' ? ('start_date' as const) : undefined,
                sortDir: filters.sortDir,
                status: mapLegacyStatusFilters(filters.status),
                mine: filters.mine,
            }

            const results = await listOperationalJobs(operationalFilters)
            let mapped = results.map(mapOperationalJobListItem)

            const customerName = filters.customerName?.trim().toLowerCase()
            if (customerName) {
                mapped = mapped.filter((job) =>
                    job.customerName.toLowerCase().includes(customerName),
                )
            }

            const serviceTypeName = filters.serviceTypeName?.trim().toLowerCase()
            if (serviceTypeName) {
                mapped = mapped.filter((job) =>
                    job.serviceTypeName.toLowerCase().includes(serviceTypeName),
                )
            }

            if (filters.createdAfter || filters.createdBefore) {
                mapped = mapped.filter((job) => {
                    const createdAt = formatBusinessTimestamp(job.createdAt, 'yyyy-MM-dd')
                    if (filters.createdAfter) {
                        const after =
                            typeof filters.createdAfter === 'string'
                                ? filters.createdAfter
                                : toApiDateString(filters.createdAfter)
                        if (createdAt < after) {
                            return false
                        }
                    }
                    if (filters.createdBefore) {
                        const before =
                            typeof filters.createdBefore === 'string'
                                ? filters.createdBefore
                                : toApiDateString(filters.createdBefore)
                        if (createdAt > before) {
                            return false
                        }
                    }
                    return true
                })
            }

            if (filters.sortBy === 'created_at') {
                const direction = filters.sortDir === 'asc' ? 1 : -1
                mapped.sort(
                    (left, right) => direction * left.createdAt.localeCompare(right.createdAt),
                )
            }

            return mapped
        },

        async getJob(id) {
            const job = await getOperationalJob(id)
            return mapOperationalJobDetail(job)
        },

        async patchJob(id, values) {
            const job = await patchJob(id, {
                name: values.jobName.trim(),
                serviceTypeId: values.serviceTypeId,
                notes: values.notes,
                ...(values.customerContactId !== undefined
                    ? {customerContactId: values.customerContactId}
                    : {}),
                ...(values.poNumber !== undefined ? {poNumber: values.poNumber} : {}),
                ...(values.projectBillingTermsId !== undefined
                    ? {projectBillingTermsId: values.projectBillingTermsId}
                    : {}),
                ...(values.specificType !== undefined ? {specificType: values.specificType} : {}),
                ...(values.plannedEndDate !== undefined
                    ? {plannedEndDate: values.plannedEndDate}
                    : {}),
            })
            return mapOperationalJobDetail(job)
        },

        async saveSchedule(id, values) {
            const job = await patchJob(id, jobScheduleInputToPatch(values))
            return mapOperationalJobDetail(job)
        },

        async saveLocations(id, locations) {
            const job = await updateJobLocations(id, locations)
            return mapOperationalJobDetail(job)
        },

        async saveRequirements(id, requirements) {
            const existing = mapOperationalJobDetail(await getOperationalJob(id))
            const {workerRequirements, assetRequirements} = mapLegacyRequirementsToOperational(
                requirements,
                existing,
            )

            // Save asset requirements first; a failed request leaves worker requirements as they were
            await updateJobAssetRequirements(id, assetRequirements)
            const job = await updateJobWorkerRequirements(id, workerRequirements)
            return mapOperationalJobDetail(job)
        },

        async saveAssignments(id, assignments) {
            const operationalJob = await getOperationalJob(id)
            const {workerAssignments, assetAssignments} = mapLegacyAssignmentsToOperational(
                assignments,
                operationalJob,
            )

            await updateJobWorkerAssignments(id, workerAssignments)
            const job = await updateJobAssetAssignments(id, assetAssignments)
            return mapOperationalJobDetail(job)
        },

        async approveJob(id) {
            const job = await patchJob(id, {status: 'active'})
            return mapOperationalJobDetail(job)
        },

        async archiveJob(id) {
            const job = await patchJob(id, {status: 'completed'})
            return mapOperationalJobDetail(job)
        },

        async cancelJob(id, reason) {
            const job = await patchJob(id, {status: 'cancelled', cancelReason: reason})
            return mapOperationalJobDetail(job)
        },

        async reopenJob(id, approvedToStart) {
            const job = await patchJob(id, {status: approvedToStart ? 'active' : 'planned'})
            return mapOperationalJobDetail(job)
        },

        async createFinancialLineItem(jobId, input) {
            const job = await createBillableLineItem(jobId, mapFinancialToBillable(input))
            return mapOperationalJobDetail(job)
        },

        async updateFinancialLineItem(jobId, lineItemId, input) {
            const job = await updateBillableLineItem(
                jobId,
                lineItemId,
                mapFinancialToBillable(input),
            )
            return mapOperationalJobDetail(job)
        },

        async archiveFinancialLineItem(jobId, lineItemId) {
            const job = await archiveBillableLineItem(jobId, lineItemId)
            return mapOperationalJobDetail(job)
        },

        async listTimeEntries(jobId) {
            return listJobTimeEntries(jobId)
        },

        async createTimeEntry(jobId, assignmentId, input) {
            await createJobTimeEntry(jobId, assignmentId, input)
            const job = await getOperationalJob(jobId)
            return mapOperationalJobDetail(job)
        },

        async updateTimeEntry(jobId, entryId, input) {
            await updateJobTimeEntry(jobId, entryId, input)
            const job = await getOperationalJob(jobId)
            return mapOperationalJobDetail(job)
        },

        async deleteTimeEntry(jobId, entryId) {
            await deleteJobTimeEntry(jobId, entryId)
            const job = await getOperationalJob(jobId)
            return mapOperationalJobDetail(job)
        },
    }
}

export const jobDataLayer = createServerJobDataLayer()
